<script setup lang="ts">
import { notebookModule } from '@/store/notebook/module'
import { userModule } from '@/store/user/module'
import { computed, onMounted, ref, watch } from 'vue'
import type { NotebookItemJSON } from '@/store/notebook/types'
import UIKit from '@pocketprep/ui-kit'
import type { TTutorState } from '@/store/tutor/state'
import NotebookTutorSession from '@/components/NotebookTutorSession.vue'
import type { TFilterOption } from '@/utils'
import { analyticsModule } from '@/store/analytics/module'
import Save from '@/assets/notebook/Save.vue'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import { useRouter } from 'vue-router'

const router = useRouter()

const {
    Tag,
    FilterDropdown,
    Icon,
    EmptyState,
    Pill,
    InfoMessage,
} = UIKit

const filterOptions = ref<TFilterOption[]>([
    {
        id: 'keyword',
        label: 'Keywords',
        enabled: false,
    },
    {
        id: 'tutor-session',
        label: 'Tutor Chats',
        enabled: false,
    },
])
const isLoading = ref(false)
const tutorSessions = ref<{ [serial: string]: TTutorState['chat'] }>({})
const expandedItemIndexes = ref(new Set<number>())

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const allNotebookItems = computed(() => {
    return notebookModule.state.notebookItems
})

const groupedItems = computed(() => {
    return notebookModule.getters.getItemsGroupedByType()
})

const visibleContent = computed(() => {
    const noFiltersSelected = !filterOptions.value.find(o => o.enabled)

    return filterOptions.value
        .flatMap(filter => {
            if (filter.enabled || noFiltersSelected) {
                return groupedItems.value.get(filter.id as NotebookItemJSON['metadata']['type'])
            }

            return []
        })
        .filter(i => !!i)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
})

const visibleFilterLabels = computed(() => {
    return filterOptions.value.reduce((acc, fo) => {
        if (fo.enabled) {
            acc.push(fo.label)
        }
        return acc
    }, new Array<string>())
})

onMounted(async () => {
    if (notebookModule.state.experimentEndDate < new Date()) {
        router.push({ name: 'study' })
        return
    }

    isLoading.value = true
    await Promise.all([
        userModule.actions.fetchUserData(),
        notebookModule.actions.fetchNotebookItems(),
    ])
    tutorSessions.value = await notebookModule.actions.fetchNotebookTutorSessions()

    // update filter options with counts
    filterOptions.value = [
        {
            id: 'keyword',
            label: `Keywords (${getContentTypeCount('keyword')})`,
            enabled: false,
        },
        {
            id: 'tutor-session',
            label: `Tutor Chats (${getContentTypeCount('tutor-session')})`,
            enabled: false,
        },
    ]

    isLoading.value = false
})

const getTutorSessionBySerial = (serial: string) => {
    return tutorSessions.value[serial]
}

const getContentTypeCount = (type: NotebookItemJSON['metadata']['type']) => {
    return groupedItems.value.get(type)?.length ?? 0
}

const toggleItem = (itemIndex: number) => {
    const isItemExanded = expandedItemIndexes.value.has(itemIndex)

    if (isItemExanded) {
        expandedItemIndexes.value.delete(itemIndex)
    } else {
        expandedItemIndexes.value.add(itemIndex)
    }
}

const removeFilter = (filterLabel: string) => {
    filterOptions.value = filterOptions.value.map(o => ({
        ...o,
        enabled: o.label === filterLabel ? false : o.enabled,
    }))
}

const canExpandItem = (itemMetadata: NotebookItemJSON['metadata']) => {
    if (itemMetadata.type === 'tutor-session') {
        const tutorSession = tutorSessions.value[itemMetadata.questionSerial] || []
        if (tutorSession.length > 2) {
            return true
        }
    }

    return false
}

watch(visibleFilterLabels, (value, oldValue) => {
    if (!oldValue.length && !value.length) {
        return
    }
    analyticsModule.actions.amplitudeTrack(
        'Saved Items Filtered',
        {
            filterOptions: visibleFilterLabels.value,
        }
    )
})
</script>

<template>
    <div v-dark class="notebook">
        <div
            id="notebook"
            v-dark
            class="notebook__head"
        >
            <h1
                v-dark
                class="notebook__title"
                role="alert"
            >
                <div class="notebook__title-text">Saved</div>
                <Tag :isBetaTag="true" class="notebook__title-tag">
                    <template #tagMessage>
                        Beta
                    </template>
                </Tag>
            </h1>
            <InfoMessage
                class="notebook__banner"
                :is-dark-mode="isDarkMode"
            >
                <strong>Thanks for trying our Save Items beta.</strong><br />
                What we learned from this beta test will help shape what we build next. Saving is now turned off.
                You can view and copy saved items until December 12, 2026.
            </InfoMessage>
            <div class="notebook__filters">
                <FilterDropdown
                    v-if="!isLoading && allNotebookItems.length"
                    class="notebook__filter-dropdown"
                    size="small"
                    align="left"
                    filterLabel="Filter by Type"
                    filterOptionsLabel="Filter by Type"
                    :radioEntries="[]"
                    :checkboxEntries="filterOptions"
                    :is-dark-mode="isDarkMode"
                />
                <div v-if="visibleFilterLabels.length" class="notebook__filter-pills">
                    <Pill v-for="filter in visibleFilterLabels"
                          :key="filter"
                          class="notebook__filter-pill"
                          :show-close="true"
                          :is-dark-mode="isDarkMode"
                          @click="removeFilter(filter)">
                        {{ filter.split('(')[0].trim() }}
                    </Pill>
                </div>
            </div>
        </div>
        <div v-dark class="notebook__body">
            <div v-if="isLoading"
                 class="notebook__items-loading"
                 v-dark>
                Loading...
            </div>
            <EmptyState
                v-else-if="!visibleContent?.length"
                class="notebook__empty-state"
                title="You don't have any saved items."
                message="Highlighted keywords and Tutor chats that you save will show up here."
                :is-dark-mode="isDarkMode"
                :customBlobColor="isDarkMode ? BrandColors.moonlitOcean : BrandColors.white"
                v-dark
            >
                <template #icon>
                    <Save :is-active="false"
                          :is-hover="false"
                          :is-dark-mode="true" />
                </template>
            </EmptyState>
            <div v-else class="notebook__items">
                <div
                    v-for="item, i in visibleContent"
                    :key="item.objectId"
                    class="notebook__item"
                    :tabindex="canExpandItem(item.metadata) ? 0 : -1"
                    v-dark
                    :class="{
                        [`notebook__item--${item.metadata.type}`]: true,
                        ['notebook__item--expanded']: expandedItemIndexes.has(i),
                        ['notebook__item--can-expand']: canExpandItem(item.metadata)
                    }"
                    @keydown.enter="toggleItem(i)"
                    @click="toggleItem(i)"
                >
                    <template v-if="item.metadata.type === 'keyword'">
                        <div class="notebook__item-label">{{ item.metadata.keyword }}</div>
                        <div class="notebook__item-description" v-dark>{{ item.metadata.definition }}</div>
                    </template>
                    <template
                        v-if="item.metadata.type === 'tutor-session'
                            && getTutorSessionBySerial(item.metadata.questionSerial)"
                    >
                        <div class="notebook__item-prompt">
                            <div class="notebook__item-label">{{ item.metadata.questionPrompt }}</div>
                            <div class="notebook__item-expand"
                                 v-if="canExpandItem(item.metadata)"
                                 v-dark>
                                <div class="notebook__item-expand-label" v-dark>
                                    <template v-if="!expandedItemIndexes.has(i)">
                                        Expand
                                    </template>
                                    <template v-else>
                                        Collapse
                                    </template>
                                </div>
                                <Icon
                                    :style="{
                                        transform: `rotate(${expandedItemIndexes.has(i) ? '180' : '0'}deg)`
                                    }"
                                    type="accordionArrow"
                                    class="exam-card__arrow"
                                />
                            </div>
                        </div>
                        <div
                            class="notebook__item-ellipsis"
                            v-dark
                            v-if="!expandedItemIndexes.has(i) && canExpandItem(item.metadata)"
                        >...</div>
                        <NotebookTutorSession
                            :isExpanded="expandedItemIndexes.has(i)"
                            :chat="getTutorSessionBySerial(item.metadata.questionSerial)"
                        />
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@mixin container-styles {
    max-width: 852px;
    width: 100%;
    padding: 0 36px;
    box-sizing: border-box;

    @include breakpoint(black-bear) {
        padding: 0 18px;
    }
}

.notebook {
    background: $gray-background;
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 54px);
    max-width: 100%;

    @include breakpoint(black-bear) {
        padding: 0 18px;
    }

    &--dark {
        background: $evening;
        color: $barely-background;
    }

    &__head {
        position: relative;
        padding: 55px 0 0;
        max-width: 680px;
        width: 100%;
        margin: 0 auto;
    }

    &__title {
        font-size: 26px;
        line-height: 34px;
        font-weight: 600;
        margin: 0 0 6px;
        padding: 0 !important;
        display: flex;
        align-items: center;

        @include container-styles;
    }

    &__subtitle {
        margin-bottom: 12px;
    }

    &__filters {
        display: flex;
        justify-content: flex-start;
        align-items: center;

        @include breakpoint(black-bear) {
            display: block;
        }
    }

    &__filter-pills {
        margin-left: 12px;

        @include breakpoint(black-bear) {
            margin-top: 6px;
            margin-left: 0;
        }
    }

    &__filter-pill {
        margin-bottom: 0;
    }

    &__title-tag {
        margin-left: 10px;
    }

    &__body {
        display: flex;
        align-items: center;
    }

    &__empty-state {
        margin: 30px auto;
        text-align: center;

        &--dark {
            color: $pewter;
        }
    }

    &__items-loading {
        text-align: center;
        margin: 15px auto;
    }

    &__items {
        max-width: 680px;
        width: 100%;
        margin: 12px auto 30px;
    }

    &__item {
        width: 100%;
        border-radius: 6px;
        padding: 7px 11px 11px;
        background-color: $white;
        margin-bottom: 10px;
        box-sizing: border-box;
        border: 1px solid $pewter;

        &--dark {
            background-color: $brand-black;
            border: 1px solid $slate;
            padding: 7px 11px 11px;
        }
    }

    &__item--can-expand {
        cursor: pointer;
        box-shadow: 0px 1px 4px 0px rgba($ash, 0.3);
        border: 1px solid rgba($pewter, 0.3);

        &:hover {
            box-shadow: 0px 1px 4px 0px rgba($brand-blue, 0.3);
            border: 2px solid $slate;
            padding: 6px 10px 10px;
        }

        &:focus {
            outline: none;
            border: 2px solid $brand-blue;
        }

        &--dark {
            box-shadow: 0px 1px 4px 0px $jet;
        }

        &--dark:hover {
            border: 2px solid $fog;
        }

        &--dark:focus {
            padding: 6px 10px 10px;
            border: 2px solid $banana-bread;
        }
    }

    &__item--expanded {
        max-height: none;
    }

    &__item-prompt {
        display: flex;
        align-items: flex-start;
    }

    &__item-expand {
        color: $brand-blue;
        font-weight: 600;
        font-size: 15px;
        line-height: 22px;
        display: flex;
        align-items: center;
        margin-left: 10px;

        &--dark {
            color: $banana-bread;
        }
    }

    &__item-expand-label {
        margin-right: 8px;
        width: 60px;
    }

    &__item-ellipsis {
        width: 22px;
        height: 14px;
        text-align: center;
        border: 1px solid rgba($pewter, 0.3);
        background-color: $fog;
        color: $ash;
        line-height: 4px;
        border-radius: 3px;
        box-sizing: border-box;
        font-weight: 600;
        margin-bottom: 10px;
    }

    &__item-label {
        font-weight: 600;
        font-size: 16px;
        line-height: 22px;
        margin-bottom: 10px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
    }

    &__item-description {
        font-weight: 500;
        font-size: 15px;
        line-height: 20px;
        color: $moonlit-ocean;

        &--dark {
            color: $white;
        }
    }

    &__banner {
        margin-bottom: 40px;

        strong {
            font-weight: 600;
        }
    }
}
</style>
